/**
 * GamReportCard — stage 0 of the monthly run, rendered as a prominent card
 * above the timeline. No upload yet → a full-width drop zone (the page's
 * visual anchor for "the GAM report dropped, start the month"). Uploaded →
 * compact stats + Add / Replace, plus the stale-report warning when the
 * reports changed after processing already ran.
 *
 * A month can have several GAM reports (Admin → Settings → GAM Report
 * Uploads sets how many are expected). They merge into one dataset, so the
 * stats are always the combined totals; with more than one report the card
 * lists each (with Remove), and a month short of its expected count shows
 * "1 of 2 reports".
 *
 * The CSV's revenue total is GROSS GAM advertiser billings — no YouTube fees
 * or splits applied yet — so once the month's DS processing run completes the
 * card also shows YouTube fees and the split pool (2 × creator earnings, the
 * revenue Culture Genesis actually splits with creators). Hidden while the
 * report is stale: the stats would describe the OLD report.
 */

import React, { useState } from 'react';
import { Button, Card, StatusPill } from '../../ui';
import { GamReportSourceMeta, GamReportUploadMeta, PipelineRun } from '../../../services/adminApi';
import { formatMonthDisplay, reportSetStatus } from './pipelineUtils';
import { DsRunStats } from './usePipelineController';

interface GamReportCardProps {
  month: string;
  uploadMeta: GamReportUploadMeta | null;
  /** GAM reports the month is expected to have (1 unless Admin → Settings says more) */
  expectedReports: number;
  pipeline: PipelineRun | null;
  /** Post-processing totals of the month's completed DS run (null until one exists) */
  dsRunStats: DsRunStats | null;
  /** Open the upload modal; dropped files skip its pick phase. No intent = decided from the month */
  onUpload: (files?: File[], intent?: 'add' | 'replace-all') => void;
  /** Remove one report of a multi-report month */
  onRemoveSource: (source: GamReportSourceMeta) => void;
  /** sourceId of the report being removed (null when idle) */
  removingSourceId: string | null;
  /** Re-run DS processing to pick up changed reports (stale warning CTA) */
  onRerunProcessing: () => void;
  rerunBusy: boolean;
}

const fmtUsd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const GamReportCard: React.FC<GamReportCardProps> = ({
  month,
  uploadMeta,
  expectedReports,
  pipeline,
  dsRunStats,
  onUpload,
  onRemoveSource,
  removingSourceId,
  onRerunProcessing,
  rerunBusy,
}) => {
  const [dragOver, setDragOver] = useState(false);

  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = Array.from(e.dataTransfer.files ?? []);
      onUpload(dropped.length ? dropped : undefined);
    },
  };

  const { count, expected, missing } = reportSetStatus(uploadMeta, expectedReports);
  const sources = uploadMeta?.sources ?? [];
  const multi = count > 1;

  // The reports changed after processing already consumed the old set
  const stale = Boolean(
    uploadMeta &&
    pipeline &&
    uploadMeta.month === pipeline.month &&
    pipeline.stages.dsProcessing.completedAt &&
    uploadMeta.uploadedAt > pipeline.stages.dsProcessing.completedAt
  );

  if (!uploadMeta) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Upload the ${formatMonthDisplay(month)} GAM Direct Sold CSV`}
        style={{
          ...styles.dropCard,
          ...(dragOver ? styles.dropCardActive : {}),
        }}
        onClick={() => onUpload()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onUpload();
        }}
        {...dropHandlers}
      >
        <div style={styles.dropTitle}>
          Drop the {formatMonthDisplay(month)} GAM CSV{expected > 1 ? 's' : ''}
        </div>
        <div style={styles.dropHint}>
          The &quot;Ad Impressions &amp; Revenue by Channel [Monthly]&quot; export from Google Ad Manager
          — or click to browse. The monthly run starts from {expected > 1 ? 'these reports' : 'this report'}.
          {expected > 1 && (
            <> {formatMonthDisplay(month)} expects {expected} reports: drop them together or one at a time.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card style={styles.card}>
      <div style={styles.headerRow} {...dropHandlers}>
        <div style={styles.fileWrap}>
          {missing > 0 ? (
            <StatusPill variant="pending">{count} of {expected} reports</StatusPill>
          ) : multi ? (
            <StatusPill variant="ok">{count} reports merged</StatusPill>
          ) : (
            <StatusPill variant="ok">report uploaded</StatusPill>
          )}
          {multi ? (
            <span style={styles.mergedNote}>Combined into one dataset for DS processing</span>
          ) : (
            <span style={styles.fileName} title={uploadMeta.fileName}>{uploadMeta.fileName}</span>
          )}
        </div>
        <div style={styles.headerActions}>
          <Button variant={missing > 0 ? 'primary' : 'ghost'} size="sm" onClick={() => onUpload(undefined, 'add')}>
            Add report
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onUpload(undefined, 'replace-all')}>
            {multi ? 'Replace all' : 'Replace report'}
          </Button>
        </div>
      </div>
      {missing > 0 && (
        <div style={styles.missingNote}>
          {formatMonthDisplay(month)} expects {expected} GAM reports — add the remaining{' '}
          {missing === 1 ? 'one' : missing} before starting processing.
        </div>
      )}
      {multi && sources.length > 0 && (
        <div style={styles.sourceList}>
          {sources.map((s) => (
            <div key={s.sourceId} style={styles.sourceRow}>
              <div style={styles.sourceMain}>
                <span style={styles.sourceName} title={s.fileName}>{s.fileName}</span>
                <span
                  style={styles.sourceMeta}
                  title={s.preamble?.filtersText ? `Filters: ${s.preamble.filtersText}` : undefined}
                >
                  {[
                    s.preamble?.reportName,
                    `uploaded ${new Date(s.uploadedAt).toLocaleDateString()} by ${s.uploadedBy}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              <span style={styles.sourceStat}>{s.rowCount.toLocaleString()} rows</span>
              <span style={styles.sourceStat}>{fmtUsd(s.totalRevenue)}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveSource(s)}
                disabled={removingSourceId !== null}
                aria-label={`Remove ${s.fileName}`}
              >
                {removingSourceId === s.sourceId ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          ))}
        </div>
      )}
      <div style={styles.statsRow}>
        <span style={styles.stat}>
          <span style={styles.statLabel}>Rows</span>
          <span style={styles.statValue}>{uploadMeta.rowCount.toLocaleString()}</span>
        </span>
        <span style={styles.stat}>
          <span style={styles.statLabel}>Channels</span>
          <span style={styles.statValue}>{uploadMeta.channelCount.toLocaleString()}</span>
        </span>
        <span style={styles.stat}>
          <span style={styles.statLabel}>Impressions</span>
          <span style={styles.statValue}>{uploadMeta.totalImpressions.toLocaleString()}</span>
        </span>
        <span style={styles.stat} title="Sum of the CSV's 'Total CPM and CPC revenue' column — what advertisers were billed in GAM, before YouTube fees and splits">
          <span style={styles.statLabel}>Gross GAM revenue</span>
          <span style={styles.statValue}>{fmtUsd(uploadMeta.totalRevenue)}</span>
        </span>
        {!stale && typeof dsRunStats?.totalYouTubeFees === 'number' && (
          <span style={styles.stat} title="YouTube's share removed by DS processing (rate card × YT split % per 1,000 impressions)">
            <span style={styles.statLabel}>YouTube fees</span>
            <span style={styles.statValue}>−{fmtUsd(dsRunStats.totalYouTubeFees)}</span>
          </span>
        )}
        {!stale && typeof dsRunStats?.totalChannelEarnings === 'number' && (
          <span style={styles.stat} title="2 × creator earnings after YouTube fees and split minimums — the revenue Culture Genesis splits 50/50 with creators">
            <span style={styles.statLabel}>Split pool</span>
            <span style={{ ...styles.statValue, color: 'var(--accent)' }}>
              {fmtUsd(dsRunStats.totalChannelEarnings * 2)}
            </span>
          </span>
        )}
        <span style={styles.uploadedBy}>
          {multi ? 'Last changed' : 'Uploaded'} {new Date(uploadMeta.uploadedAt).toLocaleDateString()} by{' '}
          {uploadMeta.uploadedBy}
        </span>
      </div>
      {stale && (
        <div style={styles.staleBanner}>
          <span>
            {multi
              ? 'The reports changed after processing ran — re-run DS processing (and any later steps) to pick them up.'
              : 'The report was replaced after processing ran — re-run DS processing (and any later steps) to pick it up.'}
          </span>
          <Button variant="ghost" size="sm" onClick={onRerunProcessing} disabled={rerunBusy}>
            {rerunBusy ? 'Starting…' : 'Re-run processing'}
          </Button>
        </div>
      )}
    </Card>
  );
};

const styles: Record<string, React.CSSProperties> = {
  dropCard: {
    border: '2px dashed var(--border-2)',
    borderRadius: 'var(--radius-lg)',
    padding: '36px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    background: 'var(--bg-surface-2)',
    transition: 'border-color var(--dur-fast) var(--ease-standard), background var(--dur-fast) var(--ease-standard)',
  },
  dropCardActive: {
    borderColor: 'var(--accent)',
    background: 'var(--accent-soft)',
  },
  dropTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.01em',
  },
  dropHint: {
    fontSize: 13,
    color: 'var(--fg-3)',
    marginTop: 8,
    maxWidth: 520,
    marginLeft: 'auto',
    marginRight: 'auto',
    lineHeight: 1.5,
  },
  card: {
    padding: '14px 18px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  fileWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flex: '1 1 0', // the file name truncates (full name in its tooltip) so the buttons stay on the row
  },
  fileName: {
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--fg-1)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mergedNote: {
    fontSize: 13,
    color: 'var(--fg-3)',
  },
  headerActions: {
    display: 'flex',
    gap: 6,
    flexShrink: 0,
  },
  missingNote: {
    marginTop: 10,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--warning)',
    lineHeight: 1.45,
  },
  sourceList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 12,
    borderTop: '1px solid var(--border-1)',
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '8px 0',
    borderBottom: '1px solid var(--border-1)',
  },
  sourceMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  sourceName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-1)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourceMeta: {
    fontSize: 12,
    color: 'var(--fg-3)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourceStat: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-2)',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  statsRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 24,
    flexWrap: 'wrap',
    marginTop: 12,
  },
  stat: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 2,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  statValue: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--fg-1)',
    fontVariantNumeric: 'tabular-nums',
  },
  uploadedBy: {
    fontSize: 12,
    color: 'var(--fg-3)',
    marginLeft: 'auto',
    alignSelf: 'flex-end',
  },
  staleBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 12,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--warning)',
    background: 'var(--warning-soft)',
    border: '1px solid var(--cg-clay-200)',
    borderRadius: 'var(--radius-md)',
    lineHeight: 1.45,
  },
};

export default GamReportCard;
